<template>
  <div
    :class="[
      'flash__message',
      `flash__message--${variant}`,
      { 'flash__message--visible': shouldBeVisible },
    ]"
  >
    <i
      v-if="!isNeutral(type)"
      :class="['flash__icon', `flash__icon--${variant}`, { 'icon-tick': isPositive(type) }]"
    ></i>
    <span class="flash__text" v-html="text.replace(/\n/g, '<br>')"></span>
    <i
      v-if="!shouldAutoHide"
      :class="`flash__close flash__close--${variant} icon-cross`"
      @click="closeMessage"
    ></i>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listenToTransitionEnd } from '@/helpers/animation'

export default {
  name: 'FlashMessage',

  props: {
    id: { type: Number, required: true },
    text: { type: String, required: true },
    type: { type: String, required: true },
    deleteMessage: { type: Function, required: true },
  },

  data() {
    return {
      shouldBeVisible: false,
    }
  },

  computed: {
    ...mapGetters({
      isPositive: 'flash/isPositive',
      isNeutral: 'flash/isNeutral',
      isNegative: 'flash/isNegative',
      typeClassification: 'flash/typeClassification',
    }),

    variant() {
      return this.typeClassification(this.type)
    },

    shouldAutoHide() {
      return !this.isNegative(this.type)
    },
  },

  mounted() {
    this.$nextTick(() => {
      imagesLoaded(this.$el, () => this.openMessage())
    })
  },

  methods: {
    openMessage() {
      this.shouldBeVisible = true

      if (this.shouldAutoHide) this.autoHideTimeout = setTimeout(() => this.closeMessage(), 5000)
    },

    closeMessage() {
      clearTimeout(this.autoHideTimeout)
      this.shouldBeVisible = false
      listenToTransitionEnd(this.$el, ['transform', 'margin-top'], () => {
        this.deleteMessage(this.id)
      })

      if (this.isFollowedByAnotherMessage()) {
        this.$el.style.transform = 'translateY(0)'
        this.$el.style.marginTop = `${this.$el.offsetHeight * -1}px`
      }
    },

    isFollowedByAnotherMessage() {
      // Makes sense since we use flex-direction: column-reverse on parent
      return this.$el.matches('.flash__message--visible ~ .flash__message')
    },
  },
}
</script>

<style scoped lang="scss">
$_message-vertical-padding: $site-gutter;
$_message-font-size: $font-size-small;
$_message-line-height-px: round($_message-font-size * $line-height);
$_icon-circle-size: 21px;
$_icon-circle-margin-top: round(($_message-line-height-px - $_icon-circle-size) / 2) + 1;
$_icon-check-height-success: 10px;
$_icon-check-height-error: 14px;
$_close-icon-size: $font-size-smaller;
$_close-icon-padding: 10px;

.flash {
  &__message {
    position: relative;
    display: flex;
    background: transparentize($color-white, 0.05);
    border-bottom: 1px solid $color-navigation-border;
    padding: $_message-vertical-padding $site-gutter-large;
    font-size: $_message-font-size;
    color: $color-text-light;
    line-height: $_message-line-height-px;
    cursor: default;
    opacity: 0;
    transform: translateY(-30px);
    @include transition((transform, margin-top, opacity), 0.8s ease-in-out);

    @include min-screen($mq-width-larger) {
      padding-left: $header-height-large + $navigation-first-link-padding-left - 2;
    }

    @include min-screen($mq-width-full) {
      padding-left: $header-height-large + $navigation-first-link-padding-left-full - 2;
    }

    &--positive,
    &--negative {
      border-bottom: 0;
      color: $color-white;
    }

    &--positive {
      background: darken($color-green, 5);
    }

    &--negative {
      background: $color-red;
    }

    &--visible,
    .is-touch-device & {
      opacity: 1;
      transform: translateY(0);
      transition-duration: 0.6s;
    }
  }

  &__icon {
    position: relative;
    display: inline-block;
    margin-top: $_icon-circle-margin-top;
    margin-right: $site-gutter - 5;
    border: 1px solid $color-white;
    border-radius: 50%;
    width: $_icon-circle-size;
    height: $_icon-circle-size;

    &:before {
      position: absolute;
      top: 50%;
      left: 50%;
      font-size: $_icon-check-height-success;
      transform: translate(-50%, -50%);
    }

    &--positive,
    &--negative {
      &:before {
        color: $color-white;
      }
    }

    &--negative {
      &:before {
        content: '!';
        font-size: $_icon-check-height-error;
        font-style: normal;
      }
    }
  }

  &__text {
    display: inline-block;
    flex: 1;
    margin-top: 2px;
    padding-right: $_close-icon-size + $site-gutter-large;
  }

  &__close {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: $site-gutter-large - $_close-icon-padding;
    margin-right: -$_close-icon-padding;
    padding: $_close-icon-padding;
    font-size: $_close-icon-size;
    color: $color-text-lighter;
    line-height: $_message-line-height-px;
    cursor: pointer;

    @include min-screen($mq-width-larger) {
      right: $navigation-last-additional-nav-padding-right - $_close-icon-padding + 5;
    }

    @include min-screen($mq-width-full) {
      right: $navigation-last-additional-nav-padding-right-full - $_close-icon-padding + 5;
    }

    &:hover {
      color: $color-text-light;
    }

    &--positive,
    &--negative {
      color: $color-white;

      &:hover {
        color: $color-white;
        opacity: 0.7;
      }
    }
  }
}
</style>
